import React, { useState, useEffect, useCallback } from "react";
import { Plus, Play, RotateCw, X, Eye, Rocket, CircleDot, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DeploymentStatusBadge, DeploymentTypeBadge } from "./deployments/DeploymentBadges";
import DeploymentFormDialog from "./deployments/DeploymentFormDialog";
import DeploymentSubNav from "./deployments/DeploymentSubNav";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function formatDuration(seconds) {
  if (seconds == null) return "—";
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return `${m}m ${rest}s`;
}

function SummaryCard({ label, value, tone = "neutral", icon: Icon, testid }) {
  const tones = {
    neutral: "text-white/70",
    running: "text-amber-300",
    success: "text-emerald-300",
    failed: "text-rose-300",
    pending: "text-sky-300",
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        {Icon ? <Icon size={14} className={tones[tone]} /> : null}
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{label}</div>
      </div>
      <div className={`text-2xl font-black font-display ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export default function PlatformDeployments() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [deps, ts] = await Promise.all([
        api.get("/platform/deployments"),
        api.get("/platform/tenants"),
      ]);
      setItems(deps.data.items || []);
      setSummary(deps.data.summary || null);
      setTenants(ts.data.items || []);
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const action = async (id, verb, body = {}) => {
    setBusyId(id + verb);
    try {
      await api.post(`/platform/deployments/${id}/${verb}`, body);
      toast.success(`Deployment ${verb} berhasil.`);
      load();
    } catch (e) { notifyError(e, `Gagal ${verb}`); }
    finally { setBusyId(null); }
  };

  const cancel = (id) => {
    const reason = window.prompt("Alasan cancel?") || "";
    action(id, "cancel", { reason });
  };

  const tenantOf = (id) => tenants.find((t) => t.tenant_id === id);

  return (
    <PlatformLayout title="Deployment">
      <div data-testid="platform-deployments-page">
        <DeploymentSubNav />
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <SummaryCard label="Total" value={summary?.total ?? 0} icon={Rocket} testid="dep-summary-total" />
          <SummaryCard label="Pending" value={summary?.pending ?? 0} tone="pending" icon={CircleDot} testid="dep-summary-pending" />
          <SummaryCard label="Running" value={(summary?.running ?? 0) + (summary?.queued ?? 0)} tone="running" icon={Loader2} testid="dep-summary-running" />
          <SummaryCard label="Success" value={summary?.success ?? 0} tone="success" icon={CheckCircle2} testid="dep-summary-success" />
          <SummaryCard label="Failed" value={summary?.failed ?? 0} tone="failed" icon={XCircle} testid="dep-summary-failed" />
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3 mb-4">
          <div className="text-xs text-white/50">Menampilkan {items.length} deployment terbaru</div>
          <Button onClick={() => setDialogOpen(true)} className="ml-auto bg-white text-black hover:bg-white/90" data-testid="btn-create-deployment">
            <Plus size={14} className="mr-1" /> Buat Deployment
          </Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
        ) : items.length === 0 ? (
          <div className="text-white/40 text-sm p-8 rounded-2xl border border-white/10 text-center">
            Belum ada deployment. Klik <strong className="text-white">+ Buat Deployment</strong> untuk mulai.
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-white/5 text-[10px] uppercase tracking-wider text-white/40 font-bold">
              <div className="col-span-3">Deployment #</div>
              <div className="col-span-2">Tenant</div>
              <div className="col-span-1">Type</div>
              <div className="col-span-1">Version</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1">Duration</div>
              <div className="col-span-1">Created</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            {items.map((dep) => {
              const t = tenantOf(dep.tenant_id);
              const canCancel = ["PENDING", "QUEUED", "RUNNING"].includes(dep.deployment_status);
              const canStart = dep.deployment_status === "PENDING";
              const canRetry = dep.deployment_status === "FAILED";
              return (
                <div key={dep.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-t border-white/5 items-center"
                  data-testid={`dep-row-${dep.id}`}>
                  <div className="col-span-3 min-w-0">
                    <Link to={`/platform/deployments/${dep.id}`} className="text-sm font-mono font-bold text-white hover:text-emerald-300 truncate block" data-testid={`dep-number-${dep.id}`}>
                      {dep.deployment_number}
                    </Link>
                    <div className="text-[10px] text-white/40 truncate">{dep.environment}</div>
                  </div>
                  <div className="col-span-2 min-w-0">
                    <div className="text-xs font-bold text-white truncate">{t?.tenant_name || dep.tenant_id.slice(0, 8)}</div>
                    <div className="text-[10px] text-white/40 font-mono">{t?.tenant_code || "—"}</div>
                  </div>
                  <div className="col-span-1"><DeploymentTypeBadge type={dep.deployment_type} size="sm" /></div>
                  <div className="col-span-1 text-xs text-white/70 font-mono">{dep.deployment_version}</div>
                  <div className="col-span-1"><DeploymentStatusBadge status={dep.deployment_status} size="sm" /></div>
                  <div className="col-span-1 text-xs text-white/60 font-mono">{formatDuration(dep.duration_seconds)}</div>
                  <div className="col-span-1 text-[11px] text-white/40">{formatDate(dep.created_at)}</div>
                  <div className="col-span-2 flex gap-1 justify-end">
                    <Link to={`/platform/deployments/${dep.id}`} className="w-7 h-7 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10"
                      title="View" data-testid={`dep-view-${dep.id}`}>
                      <Eye size={12} />
                    </Link>
                    {canStart && (
                      <button onClick={() => action(dep.id, "start", { simulate: "success" })} disabled={busyId === dep.id + "start"}
                        className="w-7 h-7 rounded-lg grid place-items-center text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
                        title="Start" data-testid={`dep-start-${dep.id}`}>
                        <Play size={12} />
                      </button>
                    )}
                    {canRetry && (
                      <button onClick={() => action(dep.id, "retry")} disabled={busyId === dep.id + "retry"}
                        className="w-7 h-7 rounded-lg grid place-items-center text-amber-400 hover:bg-amber-500/10 disabled:opacity-50"
                        title="Retry" data-testid={`dep-retry-${dep.id}`}>
                        <RotateCw size={12} />
                      </button>
                    )}
                    {canCancel && (
                      <button onClick={() => cancel(dep.id)} disabled={busyId === dep.id + "cancel"}
                        className="w-7 h-7 rounded-lg grid place-items-center text-rose-400 hover:bg-rose-500/10 disabled:opacity-50"
                        title="Cancel" data-testid={`dep-cancel-${dep.id}`}>
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DeploymentFormDialog tenants={tenants} open={dialogOpen} onClose={() => setDialogOpen(false)} onCreated={load} />
      </div>
    </PlatformLayout>
  );
}
